import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { formatCurrency } from '../../../services/campaignService';

const BudgetGoals = ({ data, updateData, errors }) => {
  const paymentScheduleOptions = [
    { value: 'upfront', label: 'Full Payment Upfront' },
    { value: 'milestone', label: 'Milestone-Based Payments' },
    { value: 'completion', label: 'Payment on Completion' },
    { value: 'split', label: '50/50 Split (Start & End)' }
  ];

  const calculateCommission = (budget) => {
    const amount = parseFloat(budget) || 0;
    const commission = amount * 0.15; // 15% platform commission
    const total = amount + commission;
    return { commission, total };
  };

  const budgetCalc = calculateCommission(data?.budget);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Budget & Goals</h2>
        <p className="text-muted-foreground">Define your campaign budget and performance targets</p>
      </div>

      <div className="space-y-4">
        <div>
          <Input
            label="Total Campaign Budget (TRY)"
            type="number"
            placeholder="10000"
            value={data?.budget || ''}
            onChange={(e) => updateData('budget', e?.target?.value)}
            error={errors?.budget}
            required
          />
          {data?.budget && parseFloat(data?.budget) > 0 && (
            <div className="mt-3 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Campaign Budget:</span>
                <span className="font-medium text-foreground">{formatCurrency(parseFloat(data?.budget))}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Platform Commission (15%):</span>
                <span className="font-medium text-foreground">{formatCurrency(budgetCalc?.commission)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between">
                <span className="font-semibold text-foreground">Total Cost:</span>
                <span className="font-bold text-primary">{formatCurrency(budgetCalc?.total)}</span>
              </div>
            </div>
          )}
        </div>

        <Input
          label="Target Cost Per Engagement (Optional)"
          type="number"
          placeholder="0.50"
          value={data?.costPerEngagement || ''}
          onChange={(e) => updateData('costPerEngagement', e?.target?.value)}
          description="Desired cost per like, comment, or share (in TRY)"
        />

        <Select
          label="Payment Schedule"
          placeholder="Select payment schedule"
          options={paymentScheduleOptions}
          value={data?.paymentSchedule || 'milestone'}
          onChange={(value) => updateData('paymentSchedule', value)}
          description="How you want to structure payments to influencers"
        />

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Campaign Goals & KPIs <span className="text-destructive">*</span>
          </label>
          <textarea
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="e.g., Reach 500K people, achieve 50K streams in first week, gain 10K new followers..."
            value={data?.goals || ''}
            onChange={(e) => updateData('goals', e?.target?.value)}
          />
          {errors?.goals && (
            <p className="text-sm text-destructive mt-1">{errors?.goals}</p>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Budget Allocation Tips</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Allocate 60-70% for influencer payments</li>
              <li>Reserve 15-20% for content production support</li>
              <li>Keep 10-15% for campaign optimization</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetGoals;
